import Button from '@app/components/common/Button';
import { IOption } from '@app/components/common/OptionsModal/types';
import { selectRoles } from '@app/state/selectors/settingsSelectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Card from '../../Card';
import Grid from '../../Grid';
import Icon from '../../Icon';
import MultiSelect from '../../MultiSelect';
import Typography from '../../Typography';
import GenericSelectionModal from '../GenericSelectionModal';
import { IItemSelection } from '../GenericSelectionModal/types';
import { ICustomSelectionModal } from '../types';
import { MenuTypeStatus } from '../MenuTypesSelectionModal/Filter/types';
import Box from '../../Box';
import { displayFlexRowStyles } from '@app/theme/commonStyles';

const UsersSelectionModal = ({
  t,
  active,
  buttons,
  onModalClose,
  onAssociate,
  btnSuccessText,
  buttonsContainerCsx,
  noCardSelectedMessage,
  selectedSectionTitle,
  ...others
}: ICustomSelectionModal<unknown>) => {
  const roles = useSelector(selectRoles);

  const [selectedUser, setSelectedUser] = useState<IItemSelection>({
    id: 0,
    title: '',
    description: '',
  });
  const [selectedRoles, setSelectedRoles] = useState<IOption[]>([]);
  const [filterValue, setFilterValue] = useState<
    { id: number; values: (number | string)[] }[]
  >([
    { id: 1, values: [MenuTypeStatus.active] },
    { id: 2, values: [2] },
  ]);

  const buttonsList = useMemo(() => {
    return buttons.map(button => ({
      ...button,
      topTagRight:
        button.extraData?.supportUser || button.extraData?.resellerUser ? (
          <Box csx={[displayFlexRowStyles, { gap: '4px' }]}>
            <Icon name="BiSupport" color="primary" size="15px" />
            <Typography variant="caption2">
              {t('commonTexts.support')}
            </Typography>
          </Box>
        ) : undefined,
    }));
  }, [buttons, t]);

  const handleOnPress = (button: IItemSelection) => {
    setSelectedUser(button);
  };

  useEffect(() => {
    const selectedUserRoles = selectedUser.roleIds;

    if (selectedUserRoles) {
      const _selectedRoles = Object.values(roles)
        .filter(role => selectedUserRoles.includes(role.id))
        .map(role => ({
          value: role.id,
          label: role.name,
        }));

      setSelectedRoles(_selectedRoles);
    } else setSelectedRoles([]);
  }, [roles, selectedUser]);

  const onFilter = (option: number, id: number) => {
    setFilterValue(prev => {
      const newFilters = [...prev];
      const filterIndex = newFilters.findIndex(filter => filter.id === id);
      if (filterIndex !== -1) {
        newFilters[filterIndex].values = [option];
      } else {
        newFilters.push({ id, values: [option] });
      }
      return newFilters;
    });
  };

  const filterUsers = useCallback(
    (
      elements: IItemSelection[],
      filters: {
        id: number;
        values: unknown[];
      }[],
    ) => {
      const status = filters[0].values[0];
      const userType = filters[1].values[0];
      const filteredUsers = elements
        .filter(user => {
          if (status === MenuTypeStatus.all) return true; // All statuses
          if (status === MenuTypeStatus.active) return user.isActive; // Active users
          if (status === MenuTypeStatus.inactive) return !user.isActive; // Inactive users
          return false;
        })
        .filter(user => {
          const userExtraData = user.extraData;
          if (userType === 1 || !userExtraData) return true; // All users
          if (userType === 2)
            return !userExtraData.supportUser && !userExtraData.resellerUser; // Normal users
          if (userType === 3)
            return userExtraData.supportUser || userExtraData.resellerUser; // Support users
          return false;
        });
      return filteredUsers;
    },
    [],
  );

  return (
    <GenericSelectionModal
      onPress={handleOnPress}
      onAssociate={onAssociate}
      onModalClose={onModalClose}
      active={active}
      buttons={buttonsList}
      currentButtonInfo={{
        id: selectedUser?.id || 0,
        roleIds: selectedRoles.map(role => role.value),
      }}
      title={t('settingsModule.usersSettings.selectionModal.title')}
      selectedSectionTitle={
        selectedSectionTitle ||
        t('settingsModule.usersSettings.selectionModal.selectedSectionTitle')
      }
      emptySectionMessage={t(
        'settingsModule.usersSettings.selectionModal.emptySectionText',
      )}
      noCardSelectedMessage={
        noCardSelectedMessage ||
        t('settingsModule.usersSettings.selectionModal.noCardSelectedText')
      }
      noAvailableCardsMessage={t(
        'settingsModule.usersSettings.selectionModal.noAvailableCardsText',
      )}
      allowAssociateMultiple
      showDescriptionPanel
      btnSuccessText={btnSuccessText}
      showSelectAll={false}
      filterOptions={[
        {
          id: 1,
          label: 'Status',
          options: [
            { label: t('commonTexts.all'), value: MenuTypeStatus.all },
            {
              label: t('commonTexts.active'),
              value: MenuTypeStatus.active,
            },
            {
              label: t('commonTexts.inactive'),
              value: MenuTypeStatus.inactive,
            },
          ],
        },
        {
          id: 2,
          label: t('commonTexts.userType'),
          options: [
            {
              label: t('commonTexts.all'),
              value: 1,
            },
            {
              label: t('commonTexts.normal'),
              value: 2,
            },
            {
              label: t('commonTexts.support'),
              value: 3,
            },
          ],
        },
      ]}
      filterValue={filterValue}
      onFilter={(option, id) => {
        onFilter(option as number, id);
      }}
      isFilterActive={true}
      alwaysRefreshSelectedItems
      buttonsContainerCsx={[
        {
          gridTemplateColumns:
            'repeat(auto-fill, minmax(160px, 1fr)) !important',
        },
        buttonsContainerCsx,
      ]}
      cardsFilterFunction={filterUsers}
      {...others}>
      {({ onSelect, isButtonSelected }) => (
        <Grid rowGap={10}>
          <Grid.Item>
            <Card.SubCard title={selectedUser.title}>
              <Grid columnGap={15} rowGap={15} csx={{ alignItems: 'center' }}>
                <Grid.Item mb={9} md={7} lg={9}>
                  <Typography
                    color="darkestGrey"
                    csx={{ wordWrap: 'break-word' }}>
                    {selectedUser.description}
                  </Typography>
                </Grid.Item>
                <Grid.Item mb={3} md={5} lg={3}>
                  {isButtonSelected ? (
                    <Button
                      csx={{ width: '100px' }}
                      icon={<Icon name="MdDeleteForever" />}
                      variant="danger"
                      onClick={() =>
                        onSelect({
                          roleIds: selectedRoles.map(role => role.value),
                        })
                      }
                    />
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() =>
                        onSelect({
                          roleIds: selectedRoles.map(role => role.value),
                        })
                      }
                      disabled={!selectedRoles.length}>
                      {t(
                        'settingsModule.usersSettings.selectionModal.selectButton',
                      )}
                    </Button>
                  )}
                </Grid.Item>
              </Grid>
            </Card.SubCard>
          </Grid.Item>
          <Grid.Item>
            <Card.SubCard
              title={t('settingsModule.usersSettings.roles')}
              csx={{ flexGrow: 1 }}>
              <MultiSelect
                options={Object.values(roles).map(role => ({
                  value: role.id,
                  label: role.name,
                }))}
                selectedOptions={selectedRoles}
                onChange={setSelectedRoles}
              />
            </Card.SubCard>
          </Grid.Item>
        </Grid>
      )}
    </GenericSelectionModal>
  );
};

export default UsersSelectionModal;
